import React from 'react';
import { Chip } from '@nextui-org/react';
import { ChevronDown, Trophy } from 'lucide-react';
import proService, { ProEvent, ProParticipant } from '../../services/proService';
import PlayerAvatar from './PlayerAvatar';

const ACCENT = '#3fced1';

interface TournamentCardProps {
  name: string;
  events: ProEvent[];
  expanded: boolean;
  onToggle: () => void;
}

/**
 * Turnier-Gruppe im Apple-Sports-Stil: Header (rundes Icon + Turniername)
 * direkt auf dem schwarzen Hintergrund, darunter ein Grid aus einzelnen
 * Match-Cards — auf Desktop maximal 4 nebeneinander.
 */
const TournamentCard: React.FC<TournamentCardProps> = ({ name, events, expanded, onToggle }) => (
  <section>
    {/* Turnier-Header */}
    <button
      onClick={onToggle}
      aria-expanded={expanded}
      className="w-full flex items-center gap-3.5 py-2 text-left group"
    >
      <div className="w-11 h-11 rounded-full border border-zinc-700 bg-zinc-900 flex items-center justify-center flex-shrink-0">
        <Trophy size={18} color={ACCENT} />
      </div>
      <span className="flex-1 text-lg font-bold text-white truncate group-hover:text-gray-200 transition-colors">
        {name}
      </span>
      <Chip
        size="sm"
        variant="flat"
        classNames={{
          base: 'h-5 px-1.5 bg-zinc-800 border border-zinc-700 flex-shrink-0',
          content: 'text-[10px] text-gray-300 px-1',
        }}
      >
        {events.length} {events.length === 1 ? 'Spiel' : 'Spiele'}
      </Chip>
      <ChevronDown
        size={18}
        className={`text-gray-500 flex-shrink-0 transition-transform duration-300 ${
          expanded ? 'rotate-180' : ''
        }`}
      />
    </button>

    {/* Match-Cards: 1 / 2 / 3 / max. 4 Spalten */}
    {expanded && (
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {events.map((event, i) => (
          <MatchCard key={proService.getEventId(event) || `${name}-${i}`} event={event} />
        ))}
      </div>
    )}
  </section>
);

// ============================================
// Einzelne Match-Card (Referenz: Apple Sports):
// links beide Spieler untereinander, vertikaler
// Trenner, rechts Uhrzeit bzw. Scores pro Zeile.
// ============================================

const MatchCard: React.FC<{ event: ProEvent }> = ({ event }) => {
  const homeName = proService.getParticipantName(event.homeParticipant);
  const awayName = proService.getParticipantName(event.awayParticipant);
  const winner = proService.getEventWinner(event);
  const isScheduled = proService.isScheduled(event);
  const isLive = proService.isLive(event);
  const status = proService.getStatusBadge(event.status);
  const averages = proService.getAverages(event);
  const homeScore = proService.getHomeScore(event) ?? 0;
  const awayScore = proService.getAwayScore(event) ?? 0;
  const time = proService.formatEventTime(event.startTime);

  const showScores = !isScheduled && status.text !== 'Verschoben' && status.text !== 'Abgesagt';

  return (
    <div className="rounded-2xl bg-zinc-800/70 border border-zinc-700/40 hover:border-zinc-600 hover:bg-zinc-800 transition-colors p-3.5">
      <div className="flex items-stretch gap-3">
        {/* Spieler-Spalte */}
        <div className="flex-1 min-w-0 flex flex-col justify-center gap-2.5">
          <PlayerLine
            participant={event.homeParticipant}
            name={homeName}
            isWinner={winner === 'home'}
            dimmed={winner === 'away'}
          />
          <PlayerLine
            participant={event.awayParticipant}
            name={awayName}
            isWinner={winner === 'away'}
            dimmed={winner === 'home'}
          />
        </div>

        {/* Vertikaler Trenner */}
        <div className="w-px bg-zinc-700/70 self-stretch flex-shrink-0" />

        {/* Rechte Spalte: Uhrzeit ODER Scores zeilengenau zu den Spielern */}
        <div className="w-12 flex-shrink-0 flex flex-col justify-center">
          {isScheduled ? (
            <span className="text-center text-base font-bold text-gray-100 tabular-nums">
              {time}
            </span>
          ) : showScores ? (
            <div className="flex flex-col gap-2.5">
              <ScoreLine value={homeScore} isWinner={winner === 'home'} dimmed={winner === 'away'} />
              <ScoreLine value={awayScore} isWinner={winner === 'away'} dimmed={winner === 'home'} />
            </div>
          ) : (
            <span className="text-center text-[10px] font-semibold text-amber-400 leading-tight">
              {status.text}
            </span>
          )}
        </div>
      </div>

      {/* Footer: LIVE-Hinweis bzw. Averages (dezent) */}
      {(isLive || (showScores && averages)) && (
        <div className="mt-2.5 pt-2 border-t border-zinc-700/40 flex items-center justify-between">
          {isLive ? (
            <span className="flex items-center gap-1.5 text-[10px] font-bold text-red-400 uppercase tracking-wider">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500" />
              </span>
              Live
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider text-gray-600">Ø 3-Dart</span>
          )}
          {averages && (
            <span className="text-[11px] text-gray-400 tabular-nums">
              {averages.home} · {averages.away}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

const PlayerLine: React.FC<{
  participant?: ProParticipant;
  name: string;
  isWinner: boolean;
  dimmed: boolean;
}> = ({ participant, name, isWinner, dimmed }) => (
  <div className="flex items-center gap-2.5 min-w-0 h-7">
    <PlayerAvatar participant={participant} />
    <span
      className={`text-sm truncate ${
        isWinner
          ? 'font-semibold text-white'
          : dimmed
          ? 'font-medium text-zinc-500'
          : 'font-medium text-gray-200'
      }`}
    >
      {name}
    </span>
    {isWinner && <Trophy size={11} color={ACCENT} className="flex-shrink-0" />}
  </div>
);

const ScoreLine: React.FC<{ value: number; isWinner: boolean; dimmed: boolean }> = ({
  value,
  isWinner,
  dimmed,
}) => (
  <span
    className={`h-7 flex items-center justify-center text-base font-bold tabular-nums ${
      isWinner ? 'text-[#3fced1]' : dimmed ? 'text-zinc-500' : 'text-white'
    }`}
  >
    {value}
  </span>
);

export default TournamentCard;
